/**
 * Tax credit note on a FINAL invoice (BE-20b) — credit chosen lines and components, amount before VAT.
 *
 * A TC-own line credits its service charge, government fee, fine and add-ons; a Thaklis line only the TC's markup.
 * The server caps each line + component at what is left and splits the total between the balance due and the wallet.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Paper,
  IconButton, Tooltip, Divider,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { formatCurrency } from '../utils/accounting/formatters';
import { invoiceMoneyService } from '../services/accounting/invoiceMoney.service';
import { serverMessage } from '../utils/apiConvert';

export const COMPONENT_LABEL = {
  SERVICE_CHARGE: 'Service charge', MARKUP: 'Markup', GOVT_FEE: 'Government fee', FINE: 'Fine', ADDON: 'Add-ons',
};
const n = (v) => Number(v || 0);

// {amount before VAT, VAT} billed for a line's component — mirrors the server's caps.
const billed = (line, component) => {
  if (!line.is_external) return component === 'MARKUP' ? [n(line.service_charge_markup), n(line.tax_markup)] : [0, 0];
  switch (component) {
    case 'SERVICE_CHARGE': return [n(line.service_charge), n(line.tax_value)];
    case 'GOVT_FEE': return [n(line.govt_charge), 0];
    case 'FINE': return [n(line.fine), 0];
    case 'ADDON': return [n(line.addon_amount), n(line.addon_vat)];
    default: return [0, 0];
  }
};

const componentsOf = (line) => Object.keys(COMPONENT_LABEL).filter((c) => billed(line, c)[0] > 0);
const lineName = (l) => `${l.service_name || l.external_service_name || 'Service'}${l.customer_name ? ` - ${l.customer_name}` : ''}`;
const emptyRow = () => ({ lineId: '', component: '', amount: '' });

const CreditNoteDialog = ({ open, onClose, invoice, creditNotes, prefill, onIssued }) => {
  const lines = (invoice?.lineItems || []).filter((l) => componentsOf(l).length > 0);
  const [reason, setReason] = useState('');
  const [rows, setRows] = useState([emptyRow()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setReason(prefill?.reason || '');
    setRows(prefill?.rows?.length ? prefill.rows.map((r) => ({ ...r, amount: String(r.amount) })) : [emptyRow()]);
  }, [open, prefill]);

  const credited = (lineId, component) => (creditNotes || []).flatMap((c) => c.lines || [])
    .filter((l) => (l.invoiceLineItemId || l.invoice_line_item_id) === lineId && l.component === component)
    .reduce((t, l) => t + n(l.amount), 0);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const lineOf = (id) => lines.find((l) => l.id === id);
  const vatOf = (r) => {
    const line = lineOf(r.lineId);
    if (!line || !r.component) return 0;
    const [net, vat] = billed(line, r.component);
    return net > 0 ? Math.round(n(r.amount) * (vat / net) * 100) / 100 : 0;
  };

  const twoDecimals = (v) => /^\d+(\.\d{1,2})?$/.test(String(v));
  const keys = rows.map((r) => `${r.lineId}:${r.component}`);
  const valid = reason.trim() && rows.length > 0
    && rows.every((r) => r.lineId && r.component && n(r.amount) > 0 && twoDecimals(r.amount))
    && new Set(keys).size === keys.length;
  const net = rows.reduce((t, r) => t + n(r.amount), 0);
  const vat = rows.reduce((t, r) => t + vatOf(r), 0);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const note = await invoiceMoneyService.issueCreditNote(invoice.id, reason.trim(),
        rows.map((r) => ({ invoiceLineItemId: r.lineId, component: r.component, amount: r.amount })));
      onIssued(note);
    } catch (err) {
      setError(serverMessage(err, 'Failed to issue the credit note'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Issue Credit Note — {invoice?.invoice_number}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A tax credit note reduces what the customer owes; any part already paid goes to their wallet. Amounts are before VAT.
        </Typography>
        <TextField label="Reason *" fullWidth size="small" sx={{ mb: 2 }} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Emirates ID service not done" />

        {rows.map((r, i) => {
          const line = lineOf(r.lineId);
          const [bNet] = line && r.component ? billed(line, r.component) : [0];
          const left = line && r.component ? Math.max(0, bNet - credited(r.lineId, r.component)) : null;
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="flex-start">
                <TextField select size="small" label="Line *" sx={{ flex: 2, minWidth: 220 }} value={r.lineId}
                  onChange={(e) => setRow(i, { lineId: e.target.value, component: componentsOf(lineOf(e.target.value))[0] || '' })}>
                  {lines.map((l) => <MenuItem key={l.id} value={l.id}>{lineName(l)}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Component *" sx={{ flex: 1, minWidth: 150 }} value={r.component} disabled={!line}
                  onChange={(e) => setRow(i, { component: e.target.value })}>
                  {(line ? componentsOf(line) : []).map((c) => <MenuItem key={c} value={c}>{COMPONENT_LABEL[c]}</MenuItem>)}
                </TextField>
                <TextField size="small" type="number" label="Amount before VAT *" sx={{ width: 170 }} inputProps={{ min: 0, step: '0.01' }}
                  value={r.amount} onChange={(e) => setRow(i, { amount: e.target.value })}
                  helperText={left != null ? `Up to ${formatCurrency(left)}` : ' '} />
                <Tooltip title="Remove">
                  <span><IconButton size="small" disabled={rows.length === 1} onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton></span>
                </Tooltip>
              </Box>
            </Paper>
          );
        })}
        <Button size="small" startIcon={<AddIcon />} onClick={() => setRows((rs) => [...rs, emptyRow()])}>Add line</Button>
        {new Set(keys).size !== keys.length && (
          <Alert severity="warning" sx={{ mt: 1 }}>Each line and component can appear once per credit note.</Alert>
        )}

        <Divider sx={{ my: 2 }} />
        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
          <Typography variant="body2">Credit before VAT: <b>{formatCurrency(net)}</b></Typography>
          <Typography variant="body2">VAT (estimated): <b>{formatCurrency(vat)}</b></Typography>
          <Typography fontWeight={700}>Total credit: {formatCurrency(net + vat)}</Typography>
          <Typography variant="caption" color="text.secondary">
            Reduces the balance due ({formatCurrency(invoice?.amount_due)}); anything beyond it goes to the customer&apos;s wallet.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={busy || !valid} onClick={submit}>Issue Credit Note</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreditNoteDialog;
